import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { toast } from 'sonner';
import PreviewBanner from '../../components/rpat/PreviewBanner';
import { 
  Search,
  ChevronRight,
  ChevronLeft,
  Check,
  Play,
  Loader2,
  Lock,
  Users,
  Globe,
  FileSearch,
  RefreshCw,
  Target,
  AlertCircle
} from 'lucide-react';

const RPATSourcingLabs = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [retakeLabId, setRetakeLabId] = useState(null);

  // Check for retake query param
  useEffect(() => {
    const retake = searchParams.get('retake');
    if (retake) {
      setRetakeLabId(retake);
      toast.info(`Retaking lab: Scroll down to find the highlighted lab`, {
        duration: 5000
      });
    }
  }, [searchParams]);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/status');
      setStatus(res.data);
      
      // Admin can always access
      const isAdmin = user?.role === 'admin';
      
      // Check if Phase 5 is completed
      if (!isAdmin && res.data.phases?.phase_5?.status !== 'completed') {
        setIsPreviewMode(true);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
      toast.error('Failed to load sourcing labs data');
    } finally {
      setLoading(false);
    }
  }, [api, user?.role]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleStartPhase6 = async () => {
    try {
      setStarting(true);
      const res = await api.post('/rpat/certification/start-phase-6');
      toast.success(res.data.message);
      fetchStatus();
    } catch (err) {
      console.error('Failed to start Phase 6:', err);
      toast.error(err.response?.data?.detail || 'Failed to start Phase 6');
    } finally {
      setStarting(false);
    }
  };

  const handleSyncProgress = async () => {
    try {
      setSyncing(true);
      const res = await api.post('/rpat/certification/sync-phase-6');
      
      if (res.data.synced_count > 0) {
        toast.success(`Synced ${res.data.synced_count} lab(s) from external completions!`, {
          description: `Labs at 70%+: ${res.data.labs_passing_threshold}/5`,
          duration: 5000
        });
        
        if (res.data.phase_6_complete) {
          setTimeout(() => {
            toast.success('Phase 6 Complete! Phase 7 is now available.', {
              duration: 6000,
              action: {
                label: 'Go to Phase 7',
                onClick: () => navigate('/rpat/certification/roleplay')
              }
            });
          }, 1500);
        }
      } else {
        // Show detailed message about why nothing synced
        const message = res.data.message || 'No new completions to sync';
        const description = res.data.external_sessions_found > 0 
          ? `Found ${res.data.external_sessions_found} session(s), ${res.data.sessions_above_threshold || 0} above threshold.`
          : 'Complete labs from the Sourcing Labs page first.';
        
        toast.info(message, {
          description: description,
          duration: 5000
        });
      }
      
      // Refresh status
      fetchStatus();
    } catch (err) {
      console.error('Failed to sync progress:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync progress');
    } finally {
      setSyncing(false);
    }
  };

  const [repairing, setRepairing] = useState(false);

  const handleRepairPhase6 = async () => {
    try {
      setRepairing(true);
      const res = await api.post('/rpat/certification/repair-phase-6');
      
      if (res.data.repairs_made?.length > 0) {
        toast.success(`Phase 6 repaired!`, {
          description: res.data.message,
          duration: 5000
        });
      } else {
        toast.info('No repairs needed');
      }
      
      // Refresh status
      fetchStatus();
    } catch (err) {
      console.error('Failed to repair Phase 6:', err);
      toast.error(err.response?.data?.detail || 'Failed to repair Phase 6');
    } finally {
      setRepairing(false);
    }
  };

  const handleRefreshLabs = async () => {
    try {
      setRefreshing(true);
      const res = await api.post('/rpat/certification/refresh-phase-6-labs');
      
      if (res.data.success) {
        toast.success(res.data.message, {
          description: `Category: ${res.data.category}. Labs with 70%+: ${res.data.labs_at_70_plus}/5`,
          duration: 6000
        });
        
        if (res.data.phase_6_complete) {
          setTimeout(() => {
            toast.success('Phase 6 Complete! Phase 7 is now available.', {
              duration: 6000,
              action: {
                label: 'Go to Phase 7',
                onClick: () => navigate('/rpat/certification/roleplay')
              }
            });
          }, 1500);
        }
      }
      
      // Refresh status
      fetchStatus();
    } catch (err) {
      console.error('Failed to refresh labs:', err);
      toast.error(err.response?.data?.detail || 'Failed to refresh labs');
    } finally {
      setRefreshing(false);
    }
  };

  const handleStartLab = async (lab) => {
    // Prevent double-clicking
    if (starting || showLoadingModal) return;
    
    // Map difficulty to standardized values
    const difficultyMapping = {
      'easy': 'beginner',
      'medium': 'intermediate',
      'hard': 'advanced',
      'very hard': 'expert',
      'beginner': 'beginner',
      'intermediate': 'intermediate',
      'advanced': 'advanced',
      'expert': 'expert'
    };
    const mappedDifficulty = difficultyMapping[(lab.difficulty || 'beginner').toLowerCase()] || 'intermediate';
    
    // Show loading modal
    setShowLoadingModal(true);
    setLoadingMessage('Initializing Sourcing Lab...');
    setStarting(true);
    
    try {
      setLoadingMessage('Generating job brief and candidates...');
      
      // Call the API to start a session for this lab
      const response = await api.post('/sourcing-labs/sessions/start', {
        lab_id: lab.id,
        difficulty: mappedDifficulty
      });
      
      if (response.data?.id) {
        setLoadingMessage('Loading session...');
        toast.success('Lab session started!');
        navigate(`/sourcing-labs/session/${response.data.id}`);
      } else {
        throw new Error('No session ID returned');
      }
    } catch (err) {
      console.error('Failed to start lab:', err);
      const errorDetail = err.response?.data?.detail;
      
      // If there's an active session, offer to continue it
      if (errorDetail?.includes?.('active session') || err.response?.status === 409) {
        try {
          setLoadingMessage('Found existing session, loading...');
          const activeRes = await api.get('/sourcing-labs/sessions/active');
          if (activeRes.data?.id) {
            toast.info('You have an active session', {
              description: 'Continuing your existing session.',
              duration: 3000
            });
            navigate(`/sourcing-labs/session/${activeRes.data.id}`);
            return;
          }
        } catch (activeErr) {
          console.error('Failed to fetch active session:', activeErr);
        }
      }
      
      setShowLoadingModal(false);
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to start lab session', {
        description: 'Please try again or start from the Sourcing Labs page.',
        duration: 5000
      });
    } finally {
      setStarting(false);
      // Don't hide modal here - let navigation handle it
    }
  };

  const handleCompleteLab = async (labId, score = 85) => {
    try {
      const res = await api.post(`/rpat/certification/complete-lab/${labId}`, { score });
      toast.success(res.data.message);
      
      if (res.data.phase_6_complete) {
        toast.success('Phase 6 Complete! Phase 7 is now available.', {
          duration: 5000,
          action: {
            label: 'Go to Phase 7',
            onClick: () => navigate('/rpat/certification/roleplay')
          }
        });
      }
      
      fetchStatus();
    } catch (err) {
      console.error('Failed to complete lab:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete lab');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      </MainLayout>
    );
  }

  const phase6 = status?.phases?.phase_6 || {};
  const labs = phase6.labs || [];
  const completedCount = phase6.completed_labs || 0;
  const progressPct = (completedCount / 5) * 100;
  const isStarted = phase6.status === 'in_progress' || phase6.status === 'completed';
  
  // Detect broken state: status is completed/in_progress but no labs
  const isBrokenState = isStarted && labs.length === 0;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/rpat')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to R-PAT
          </Button>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <PreviewBanner 
            message="Complete Phase 5 (Job Category Mastery) to unlock this phase."
            actionLabel="Go to Phase 5"
            onAction={() => navigate('/rpat/certification/job-mastery')}
          />
        )}

        {/* Broken State Repair Banner */}
        {isBrokenState && (
          <Card className="border-red-300 bg-red-50/50 dark:bg-red-950/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/50">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-red-700">Phase 6 data needs repair</p>
                    <p className="text-sm text-muted-foreground">
                      Your Phase 6 status shows as {phase6.status} but no labs are loaded. Click Repair to fix this.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleRepairPhase6}
                  disabled={repairing}
                  className="bg-red-500 hover:bg-red-600"
                >
                  {repairing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Repairing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Repair Phase 6
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Title Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600">
                <Search className="h-6 w-6 text-white" />
              </div>
              Phase 6: Sourcing Labs
            </h1>
            <p className="text-muted-foreground mt-1">
              Complete 5 sourcing challenges from your <span className="font-medium text-amber-600">{status?.selected_job_category_name || 'selected'}</span> category
            </p>
          </div>
          <Badge 
            className={`text-base px-4 py-2 ${
              phase6.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
          >
            {completedCount}/5 Completed
          </Badge>
        </div>

        {/* Progress Card */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Phase 6 Progress</span>
              <span className="text-sm text-muted-foreground">{Math.round(progressPct)}%</span>
            </div>
            <Progress value={progressPct} className="h-3" />
            {phase6.status === 'completed' && (
              <div className="flex items-center gap-2 mt-3 text-emerald-600">
                <Check className="h-5 w-5" />
                <span className="font-medium">Phase 6 Complete! Proceed to Phase 7.</span>
                <Button 
                  size="sm" 
                  className="ml-auto bg-emerald-500"
                  onClick={() => navigate('/rpat/certification/roleplay')}
                >
                  Go to Phase 7
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sync Progress Card - Show when Phase 6 is started (including completed with issues) */}
        {isStarted && (
          <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/50">
                    <RefreshCw className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium">Completed Sourcing Labs elsewhere?</p>
                    <p className="text-sm text-muted-foreground">
                      If you completed any sourcing labs outside of R-PAT, click Sync to import your progress.
                      <span className="font-medium text-amber-600 ml-1">
                        (Minimum score: 70%)
                      </span>
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleSyncProgress}
                  disabled={syncing}
                  className="border-amber-300 hover:bg-amber-100"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Progress
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Refresh Labs Card - Show when Phase 6 is started but having issues */}
        {isStarted && (
          <Card className="border-violet-200 bg-violet-50/50 dark:bg-violet-950/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-violet-100 dark:bg-violet-900/50">
                    <AlertCircle className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium">Labs not matching your category?</p>
                    <p className="text-sm text-muted-foreground">
                      If your assigned labs don&apos;t match your Phase 5 selection ({status?.selected_job_category_name || 'your category'}), 
                      refresh to get correctly matched labs.
                      <span className="font-medium text-violet-600 ml-1">
                        Completed labs with 70%+ will be preserved.
                      </span>
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleRefreshLabs}
                  disabled={refreshing}
                  className="border-violet-300 hover:bg-violet-100"
                >
                  {refreshing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh Labs
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Start Phase or Labs List */}
        {(!isStarted || isBrokenState) && !isBrokenState ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="p-4 rounded-full bg-amber-100 w-fit mx-auto mb-4">
                <Search className="h-12 w-12 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Ready to Start Sourcing Labs?</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                We&apos;ll select 5 sourcing challenges related to {status?.selected_job_category_name}. 
                Practice finding candidates, analyzing job requirements, and building talent pipelines.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-600"
                onClick={handleStartPhase6}
                disabled={starting}
              >
                {starting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Preparing Labs...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-5 w-5" />
                    Start Phase 6
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Your 5 Sourcing Labs</h2>
            <p className="text-sm text-muted-foreground">
              Each lab focuses on sourcing candidates for specific roles. Score <span className="font-semibold text-amber-600">70%+</span> on 5 labs to unlock Phase 7.
              {status?.external_completions?.sourcing_labs?.completed_count > 0 && (
                <span className="text-emerald-600 font-medium ml-1">
                  You have {status.external_completions.sourcing_labs.completed_count} labs already completed from previous sessions!
                </span>
              )}
            </p>
            
            {/* Threshold Info Card */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/50">
                    <Target className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Passing Requirement: 70% or higher</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Labs scored below 70% don&apos;t count toward Phase 6 completion. You can retry any lab to improve your score.
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-600">{labs.filter(l => (l.score || l.external_score || 0) >= 70).length}/5</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">Labs Passed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {labs.map((lab, index) => {
                const isCompleted = lab.status === 'completed';
                const completedExternally = lab.completed_externally;
                const labScore = lab.score || lab.external_score || 0;
                const passedThreshold = labScore >= 70;
                const needsRetry = lab.status === 'attempted' || (lab.last_attempt_score && lab.last_attempt_score < 70);
                const isRetakeTarget = retakeLabId === lab.id;
                const needsImprovement = isCompleted && !passedThreshold;

                return (
                  <Card 
                    key={lab.id}
                    id={`lab-${lab.id}`}
                    className={`transition-all ${
                      isRetakeTarget
                        ? 'ring-2 ring-amber-500 bg-amber-50 border-amber-300'
                        : needsImprovement
                          ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                          : isCompleted 
                            ? completedExternally 
                              ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800'
                              : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800'
                            : needsRetry
                              ? 'bg-amber-50 border-amber-300 dark:bg-amber-950/20 dark:border-amber-700'
                              : 'hover:shadow-md hover:border-amber-300'
                    }`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        {/* Number Badge */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          isCompleted 
                            ? completedExternally 
                              ? 'bg-blue-500 text-white'
                              : 'bg-emerald-500 text-white' 
                            : needsRetry
                              ? 'bg-amber-500 text-white'
                              : 'bg-amber-100 text-amber-600'
                        }`}>
                          {isCompleted ? <Check className="h-5 w-5" /> : index + 1}
                        </div>

                        {/* Lab Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{lab.title}</h3>
                            <Badge variant="outline" className="text-xs">
                              {lab.difficulty}
                            </Badge>
                            {completedExternally && (
                              <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50">
                                Already Completed
                              </Badge>
                            )}
                            {isRetakeTarget && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Needs Improvement
                              </Badge>
                            )}
                            {needsImprovement && !isRetakeTarget && (
                              <Badge className="text-xs bg-red-100 text-red-700 border-red-300">
                                Below 70%
                              </Badge>
                            )}
                            {needsRetry && !isCompleted && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50">
                                Retry Needed (scored {lab.last_attempt_score || labScore}%)
                              </Badge>
                            )}
                            {isCompleted && passedThreshold && !isRetakeTarget && (
                              <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/50">
                                Passed ({labScore}%)
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {lab.description}
                          </p>
                          {isCompleted && (
                            <p className={`text-xs mt-1 ${
                              needsImprovement 
                                ? 'text-red-600' 
                                : completedExternally 
                                  ? 'text-blue-600' 
                                  : 'text-emerald-600'
                            }`}>
                              {completedExternally ? 'Previously completed' : 'Completed'}
                              {labScore > 0 && ` • Score: ${labScore}%`}
                              {needsImprovement && ' — Retake to improve'}
                            </p>
                          )}
                          {needsRetry && !isCompleted && (
                            <p className="text-xs mt-1 text-amber-600">
                              Score 70%+ required to pass. Your last attempt: {lab.last_attempt_score || labScore}%
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {!isCompleted ? (
                            <Button
                              size="sm"
                              className={needsRetry 
                                ? "bg-amber-500 hover:bg-amber-600 text-white"
                                : "bg-gradient-to-r from-amber-500 to-orange-600"
                              }
                              onClick={() => handleStartLab(lab)}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              {needsRetry ? 'Retry Lab' : 'Start Lab'}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-emerald-600 border-emerald-300"
                              onClick={() => handleStartLab(lab)}
                            >
                              <FileSearch className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Lab Skills */}
                      {!isCompleted && (
                        <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            <span>Boolean Search</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span>Talent Pipeline</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileSearch className="h-4 w-4" />
                            <span>Resume Analysis</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions for Admin Demo/Testing Only */}
        {user?.role === 'admin' && labs.length > 0 && labs.some(l => l.status !== 'completed') && (
          <Card className="border-dashed border-amber-300 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-sm text-amber-700 mb-3">
                <strong>Admin Tools:</strong> Quick complete labs for testing
              </p>
              <div className="flex flex-wrap gap-2">
                {labs.filter(l => l.status !== 'completed').map(lab => (
                  <Button
                    key={lab.id}
                    size="sm"
                    variant="outline"
                    onClick={() => handleCompleteLab(lab.id)}
                  >
                    Complete {lab.title?.substring(0, 20)}...
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Loading Modal - Prevents double-clicks */}
      <Dialog open={showLoadingModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideClose>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg">Loading Sourcing Lab</h3>
              <p className="text-sm text-muted-foreground">{loadingMessage}</p>
              <p className="text-xs text-muted-foreground">This may take 5-10 seconds...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default RPATSourcingLabs;
